import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataGrid from "@dtci/spellbook/DataGrid";
import Button from "@dtci/spellbook/Button";
import styles from "./pptDiscountDetailsGrid.module.scss";
import tableStyles from "../../../../components/AGGrid/details-table.module.scss";
import { defaultColumnDef, columnDefs } from "./config/columns";
import { clientDiscountColumnDefs } from "./config/clientDiscountColumns";
import { buildClientDiscountRows } from "./clientDiscountRows";
import { PPTDiscountClientPanel } from "./PPTDiscountClientPanel";
import { usePPTState, usePPTActions } from "tap/store/ppt/usePPT";
import {
  useAgGridExternalFilter,
  useColumnStatePreferences,
} from "tap/components/AGGrid/hooks";
import { COLUMN_STATE_STORAGE_KEYS } from "tap/utils/gridColumnPreferences";
import { usePPTToasts } from "tap/pages/PPT/hooks/usePPTToasts";
import { STAT_TILE_FILTER } from "ppt/constants";
import { fetchDiscountDetails, exportPromotionDiscounts } from "ppt/api";

/**
 * Two lenses on the same data:
 *   - "discounts"        : Discount-as-offering. Each row is a Discount with
 *                          rolled-up scalar values; per-client breakdown is
 *                          drill-down via master/detail expansion.
 *   - "clientDiscounts"  : ClientDiscount-as-deliverable. Flat grid where each
 *                          row is one client × discount realization. Parent
 *                          context (title/studio/format/territory) is
 *                          denormalized onto each row for sort/filter.
 *
 * Sort/filter/page semantics live wholly within whichever tab is active,
 * because each tab's primary key is the row entity itself — no hybrid layer.
 */
const LENS_DISCOUNTS = "discounts";
const LENS_CLIENT_DISCOUNTS = "clientDiscounts";

// Module-level so these identities never change. Everything reachable from the
// `agGrid` prop has to be stable: ag-grid-react diffs its props by reference and
// re-applies whatever looks changed, so a fresh object here on every render
// rebuilds the side bar (closing an open tool panel) and re-runs flex sizing.
const SIDE_BAR = {
  toolPanels: [
    "filters",
    {
      id: "columns",
      labelDefault: "Columns",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressPivotMode: true,
        suppressRowGroups: true,
        suppressValues: true,
      },
    },
  ],
};

const ROW_CLASS_RULES = {
  "inactive-discount": (params) => params.data?.active === false,
};

const getRowId = (params) => String(params.data?.id);

// Hide the chevron entirely for discounts with zero clients — operator
// sees "no expander" → "no per-client breakdown to look at." Avoids the
// empty-detail-panel state.
const isRowMaster = (data) =>
  Array.isArray(data?.children) && data.children.length > 0;

export const PPTDiscountDetailsGrid = ({ gridRef, onColumnStateChange }) => {
  const {
    discountDetails,
    discountDetailsSearch,
    lifecycleFilter,
    promotionDetail,
  } = usePPTState();
  const {
    openDiscountDetailsDrawer,
    updateDiscountDetails,
    setDiscountDetailsLoading,
    openClientDiscountDrawer,
  } = usePPTActions();

  // Quick-filter driven by the lifecycle widget's stat tiles. The severity tiles
  // only match active discounts, mirroring the rollup math — disabled discounts'
  // issues are excluded from the gate-relevant counts and so from these tiles too.
  const matchesLifecycleFilter = useCallback(
    (rowData) => {
      if (lifecycleFilter === STAT_TILE_FILTER.DISABLED) {
        return rowData?.active === false;
      }
      if (rowData?.active === false) return false;
      return rowData?.issueSeverity === lifecycleFilter;
    },
    [lifecycleFilter],
  );
  const { isExternalFilterPresent, doesExternalFilterPass } =
    useAgGridExternalFilter(gridRef, lifecycleFilter, matchesLifecycleFilter);

  const { showErrorToast } = usePPTToasts();

  const [lens, setLens] = useState(LENS_DISCOUNTS);
  const [exportingDiscounts, setExportingDiscounts] = useState(false);
  const isClientLens = lens === LENS_CLIENT_DISCOUNTS;

  // Each lens keeps its own saved column layout, but the two share one DataGrid
  // and one gridRef — so this is one hook pointed at the active lens, not one
  // hook per lens. Two instances on the same ref would both listen to the mounted
  // grid and both read its column state, so a resize in either lens would be
  // persisted under both keys and clobber the layout the user saved in the other.
  const {
    initialGridState,
    isLayoutRestored,
    resetColumnState,
    hasSavedColumnState,
    isGridReady,
  } = useColumnStatePreferences(
    isClientLens
      ? COLUMN_STATE_STORAGE_KEYS.discountDetailsClientDiscounts
      : COLUMN_STATE_STORAGE_KEYS.discountDetailsDiscounts,
    gridRef,
    isClientLens ? clientDiscountColumnDefs : columnDefs,
  );

  // Kept out of sight until the saved widths are on it — see isLayoutRestored.
  // Hidden in place rather than held back, because the grid has to be mounted
  // before those widths can be applied at all. This covers the lens flip as well
  // as the first load: the `key` below remounts the grid, and the new lens's
  // widths land the same paint late as the first one's did.
  const gridClassName = [
    tableStyles.dataGrid,
    styles.pptDiscountDetailsGrid,
    isLayoutRestored ? null : tableStyles.awaitingLayout,
  ]
    .filter(Boolean)
    .join(" ");

  // Pass column state controls up to parent page. The parent's Reset Columns
  // dropdown lives outside this component, so it needs these values to know
  // whether resetting is meaningful (there's a grid and it has a saved layout).
  useEffect(() => {
    if (onColumnStateChange) {
      onColumnStateChange({
        resetColumnState,
        hasSavedColumnState,
        isGridReady,
      });
    }
  }, [onColumnStateChange, resetColumnState, hasSavedColumnState, isGridReady]);

  const handleExportDiscounts = useCallback(async () => {
    if (!promotionDetail?.id) return;
    setExportingDiscounts(true);
    try {
      await exportPromotionDiscounts(promotionDetail.id);
    } catch (error) {
      console.error("Failed to export discounts:", error);
      showErrorToast("Failed to export discounts. Please try again.");
    } finally {
      setExportingDiscounts(false);
    }
  }, [promotionDetail?.id, showErrorToast]);

  const openDrawer = useCallback(
    async (discountId) => {
      if (!discountId) return;
      openDiscountDetailsDrawer(discountId);
      setDiscountDetailsLoading(true);
      try {
        const data = await fetchDiscountDetails(discountId);
        updateDiscountDetails(data);
      } catch (error) {
        console.error("Error fetching discount details:", error);
        showErrorToast("Failed to load discount details. Please try again.");
      } finally {
        setDiscountDetailsLoading(false);
      }
    },
    [
      openDiscountDetailsDrawer,
      updateDiscountDetails,
      setDiscountDetailsLoading,
      showErrorToast,
    ],
  );

  // Row-level click dispatch — one rule everywhere:
  //   discount rows expand/collapse; client discount rows open their drawer
  //   (same as the inline client panel). The actions cell stops propagation
  //   so its click goes to OpenDrawerCellRenderer, not here.
  const handleRowClick = useCallback(
    (params) => {
      // Ignore clicks on buttons (the actions column has a button)
      const clickedButton =
        params.event?.target?.tagName === "BUTTON" ||
        params.event?.target?.closest("button");
      if (clickedButton) {
        return;
      }

      if (params.data?.parentDiscountId) {
        openClientDiscountDrawer(params.data);
        return;
      }
      if (params.node?.master) {
        params.node.setExpanded(!params.node.expanded);
      }
    },
    [openClientDiscountDrawer],
  );

  // Custom detail panel — replaces AG Grid's default sub-grid renderer with
  // our own React component (PPTDiscountClientPanel). See that file for the
  // hybrid layout: comparison strip on top + per-client breakdown table.
  // Because we provide `detailCellRenderer`, AG Grid no longer constructs an
  // internal detail grid, so detailGridOptions / getDetailRowData are unused.

  // Flat row data for the Client Discounts lens. Computed only when that
  // lens is active so the Discounts lens doesn't pay for the flatten.
  const clientDiscountRows = useMemo(
    () =>
      lens === LENS_CLIENT_DISCOUNTS
        ? buildClientDiscountRows(discountDetails)
        : null,
    [lens, discountDetails],
  );

  // Shared with all cell renderers (in particular OpenDrawerCellRenderer
  // reads context.openDrawer for the action cell click).
  const discountsContext = useMemo(() => ({ openDrawer }), [openDrawer]);
  const clientDiscountsContext = useMemo(
    () => ({ openDrawer, openClientDiscountDrawer }),
    [openDrawer, openClientDiscountDrawer],
  );

  const discountsAgGridProps = useMemo(
    () => ({
      ref: gridRef,
      rowData: discountDetails,
      resetRowDataOnUpdate: true,
      headerHeight: 45,
      suppressContextMenu: true,
      functionsReadOnly: false,
      suppressMovableColumns: false,
      sideBar: SIDE_BAR,
      getRowId,
      quickFilterText: discountDetailsSearch,
      onRowClicked: handleRowClick,
      isExternalFilterPresent,
      doesExternalFilterPass,
      context: discountsContext,
      masterDetail: true,
      detailCellRenderer: PPTDiscountClientPanel,
      // Master row sizes itself to whatever the panel renders (which sizes to
      // its content via normal CSS). Without this AG Grid uses a fixed default
      // (~300px) and small panels leave empty space.
      detailRowAutoHeight: true,
      isRowMaster,
      keepDetailRows: true,
      keepDetailRowsCount: 10,
      rowClassRules: ROW_CLASS_RULES,
    }),
    [
      gridRef,
      discountDetails,
      discountDetailsSearch,
      handleRowClick,
      isExternalFilterPresent,
      doesExternalFilterPass,
      discountsContext,
    ],
  );

  const clientDiscountsAgGridProps = useMemo(
    () => ({
      ref: gridRef,
      rowData: clientDiscountRows,
      resetRowDataOnUpdate: true,
      headerHeight: 45,
      suppressContextMenu: true,
      functionsReadOnly: false,
      suppressMovableColumns: false,
      sideBar: SIDE_BAR,
      getRowId,
      quickFilterText: discountDetailsSearch,
      onRowClicked: handleRowClick,
      isExternalFilterPresent,
      doesExternalFilterPass,
      context: clientDiscountsContext,
      rowClass: "clickable-row",
      rowClassRules: ROW_CLASS_RULES,
    }),
    [
      gridRef,
      clientDiscountRows,
      discountDetailsSearch,
      handleRowClick,
      isExternalFilterPresent,
      doesExternalFilterPass,
      clientDiscountsContext,
    ],
  );

  return (
    <div className={styles.pptDiscountDetailsGridWrapper}>
      <div className={styles.headerRow}>
        <div className={styles.lensTabs} role="tablist" aria-label="Lens">
          <button
            type="button"
            role="tab"
            aria-selected={!isClientLens}
            className={!isClientLens ? styles.lensTabActive : styles.lensTab}
            onClick={() => setLens(LENS_DISCOUNTS)}
          >
            Discounts
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isClientLens}
            className={isClientLens ? styles.lensTabActive : styles.lensTab}
            onClick={() => setLens(LENS_CLIENT_DISCOUNTS)}
          >
            Client Discounts
          </button>
        </div>
        <Button
          styleType="link"
          iconPlacement="before"
          iconType="fa-download"
          onClick={handleExportDiscounts}
          isFetching={exportingDiscounts}
          title="Export all discounts to Excel"
        >
          Export
        </Button>
      </div>

      <DataGrid
        // Force a fresh AG Grid instance when the lens flips. masterDetail is
        // an init-time option and switching mid-flight leaves the grid in an
        // inconsistent state. The key change makes React unmount the old
        // DataGrid and mount a new one with the right config from the start.
        key={lens}
        className={gridClassName}
        // Applied while AG Grid builds its columns, so the saved order is the
        // first thing painted. Nothing else contributes initial state here —
        // these grids keep their sort and filter in the grid, not the URL — so
        // the hook's fragment goes straight through, and the `key` above means a
        // lens flip mounts a fresh grid with the other lens's layout already in
        // hand rather than reordering a grid the user is looking at.
        initialState={initialGridState ?? undefined}
        autoSizing
        deltaRowDataMode={true}
        agGrid={
          isClientLens ? clientDiscountsAgGridProps : discountsAgGridProps
        }
        bordered="none"
        columnDefs={isClientLens ? clientDiscountColumnDefs : columnDefs}
        defaultColumnDef={defaultColumnDef}
        noResultsMessage={
          isClientLens ? "No Client Discounts Found" : "No Discounts Found"
        }
        dataMode="client"
        filterable
        sortable
      />
    </div>
  );
};
